'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueries } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useProtectedWorkspace } from '@/providers/protected-workspace-boundary';
import { COMPACTED_TRANSCRIPT_ARGS } from '@/components/org-chat/org-thread-message-source';
import { INITIAL_THREAD_MESSAGES_PAGE_SIZE } from '@/components/org-chat/thread-message-page-size';

/** How long a hovered row keeps its transcript warm. */
export const HOVER_PREFETCH_HOLD_MS = 10_000;
/** Most transcripts held warm at once; the least recently requested goes. */
export const MAX_PREFETCHED_TRANSCRIPTS = 8;

export type ThreadMessagePrefetch = (
  routeRef: string | undefined,
  holdMs?: number
) => void;

const PrefetchContext = createContext<ThreadMessagePrefetch>(() => {});
type Target = { routeRef: string; expiresAt: number };
type Targets = { userId: string; orgSlug: string; targets: Target[] };

export const useThreadMessagePrefetch = () => useContext(PrefetchContext);

/** Adds or extends one target, keeping the most recent requests in order. */
export function nextPrefetchTargets(
  targets: readonly Target[],
  routeRef: string,
  expiresAt: number
): Target[] {
  const existing = targets.find((target) => target.routeRef === routeRef);
  const next = targets.filter((target) => target.routeRef !== routeRef);
  next.push({
    routeRef,
    expiresAt: Math.max(expiresAt, existing?.expiresAt ?? 0),
  });
  return next.slice(-MAX_PREFETCHED_TRANSCRIPTS);
}

export function ThreadMessagePrefetchProvider({
  orgSlug,
  children,
}: {
  orgSlug: string;
  children: ReactNode;
}) {
  const workspace = useProtectedWorkspace(orgSlug);
  const userId = workspace?.userId;
  const [held, setHeld] = useState<Targets | null>(null);
  const prefetch = useCallback<ThreadMessagePrefetch>(
    (routeRef, holdMs = HOVER_PREFETCH_HOLD_MS) => {
      const ref = routeRef?.trim();
      if (!userId || !ref) return;
      const expiresAt = Date.now() + holdMs;
      setHeld((previous) => {
        const scoped =
          previous?.userId === userId && previous.orgSlug === orgSlug
            ? previous.targets
            : [];
        const current = scoped.find((target) => target.routeRef === ref);
        // A repeat request still refreshes recency (a hover on a warmed chat
        // must not be the next one evicted); only a no-op keeps the state.
        const isNewest = scoped.at(-1)?.routeRef === ref;
        if (current && isNewest && current.expiresAt >= expiresAt && previous)
          return previous;
        return {
          userId,
          orgSlug,
          targets: nextPrefetchTargets(scoped, ref, expiresAt),
        };
      });
    },
    [userId, orgSlug]
  );
  const scopedTargets =
    held && held.userId === userId && held.orgSlug === orgSlug
      ? held.targets
      : null;
  const queries = useMemo((): Parameters<typeof useQueries>[0] => {
    if (!scopedTargets) return {};
    return Object.fromEntries(
      scopedTargets.map((target) => [
        `transcript:${target.routeRef}`,
        {
          query: api.organizations.threads.queries.listThreadMessages,
          args: {
            routeRef: target.routeRef,
            ...COMPACTED_TRANSCRIPT_ARGS,
            // convex-helpers/cache uses id 0 for its first page. Keep the exact
            // args so the destination adopts this reactive subscription.
            paginationOpts: {
              cursor: null,
              numItems: INITIAL_THREAD_MESSAGES_PAGE_SIZE,
              id: 0,
            },
          },
        },
      ])
    );
  }, [scopedTargets]);
  // One page per target; never follow cursors. useQueries keeps denied or
  // failed prefetches from throwing through the sidebar. The destination owns
  // its normal error handling and the server still authorizes every read.
  useQueries(queries);
  useEffect(() => {
    if (held && (held.userId !== userId || held.orgSlug !== orgSlug)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- discard invalidated navigation hints; the render above already skips their queries
      setHeld(null);
    }
  }, [held, userId, orgSlug]);
  useEffect(() => {
    if (!held || held.targets.length === 0) return;
    const nextExpiry = Math.min(...held.targets.map((t) => t.expiresAt));
    const timer = window.setTimeout(
      () => {
        const now = Date.now();
        setHeld((previous) =>
          previous
            ? {
                ...previous,
                targets: previous.targets.filter((t) => t.expiresAt > now),
              }
            : previous
        );
      },
      Math.max(0, nextExpiry - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [held]);

  return (
    <PrefetchContext.Provider value={prefetch}>
      {children}
    </PrefetchContext.Provider>
  );
}
